import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  Typography,
} from "@mui/material";
import { CalendarDays, Plus, Search, Users } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import AgentsTable from "./AgentsTable";
import AgentForm from "./AgentForm";
import WeeklyRoster from "./WeeklyRoster";
import useDebounced from "../../../utils/useDebounced";

// Create, edit and deactivate the agent logins. Removal is a deactivation:
// attendance history is kept, the agent just can no longer sign in. The
// "Weekly Roster" view plans each agent's shifts and week-offs per week.

const PAGE_SIZE = 15;

export default function AgentRoster() {
  const [view, setView] = useState("agents");
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search);
  const [statusFilter, setStatusFilter] = useState("all");

  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Searching and filtering happen on the server alongside paging -- filtering
  // a single page in the browser would only ever search what is on screen.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(apiEndPointsConfig.agents, {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          status: statusFilter === "all" ? undefined : statusFilter,
        },
      });
      setAgents(data?.data || []);
      setPages(data?.pagination?.pages || 1);
      setTotal(data?.pagination?.total || 0);
      setStats(data?.stats || null);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load agents");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // A new search or filter invalidates the current page number: page 4 of the
  // old result set is rarely page 4 of the new one, and is often past the end.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const submit = async (values, resetForm) => {
    setSaving(true);
    setError("");
    try {
      if (editing?.id) {
        await axiosInstance.put(`${apiEndPointsConfig.agents}/${editing.id}`, values);
        setNotice(`${values.name || values.userid} updated`);
      } else {
        await axiosInstance.post(apiEndPointsConfig.agents, values);
        setNotice(`${values.name || values.userid} can now log in at /agent/login`);
      }
      resetForm();
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save the agent");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await axiosInstance.delete(`${apiEndPointsConfig.agents}/${deleting.id}`);
      setNotice(`${deleting.name || deleting.userid} removed`);
      setDeleting(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not remove the agent");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={view}
        onChange={(e, v) => v && setView(v)}
        sx={{
          mb: 2,
          "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 2 },
          "& .Mui-selected": { color: "#0C237B !important", bgcolor: "#eef2ff !important" },
        }}
      >
        <ToggleButton value="agents">
          <Users size={16} style={{ marginRight: 6 }} /> Agents
        </ToggleButton>
        <ToggleButton value="roster">
          <CalendarDays size={16} style={{ marginRight: 6 }} /> Weekly Roster
        </ToggleButton>
      </ToggleButtonGroup>

      {view === "roster" ? (
        <WeeklyRoster />
      ) : (
      <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder="Search agents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#9ca3af" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(e, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        {stats && (
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            {stats.active} active · {stats.inactive} inactive
          </Typography>
        )}

        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          sx={{ bgcolor: "#0C237B", "&:hover": { bgcolor: "#1a3399" }, borderRadius: "8px" }}
        >
          Create Agent
        </Button>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <AgentsTable
            agents={agents}
            onEdit={(a) => {
              setEditing(a);
              setFormOpen(true);
            }}
            onDelete={setDeleting}
          />

          {total > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mt: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} of {total}
              </Typography>
              {pages > 1 && (
                <Pagination
                  count={pages}
                  page={page}
                  onChange={(e, v) => setPage(v)}
                  size="small"
                  shape="rounded"
                />
              )}
            </Box>
          )}
        </>
      )}
      </>
      )}

      <AgentForm
        open={formOpen}
        agent={editing}
        saving={saving}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={submit}
      />

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove agent?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleting?.name || deleting?.userid}</strong> will no longer be able to log in.
            Their attendance history is kept, and any shift still running will be closed now.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleting(null)} sx={{ color: "#6b7280" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={confirmDelete}
            sx={{ borderRadius: "8px" }}
          >
            {saving ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
